import useRequest from "@/lib/request/useRequest";
import {
    Button,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    Selection,
    SelectItem,
    Textarea,
} from "@heroui/react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApplyNChip from "../common/ApplyNChip";
import DoNotApplyNChip from "../common/DoNotApplyNChip";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { FieldRecommendation } from "@/types/field";

function UserRecommendationForm({
    fieldId,
    onClose,
    platformRecommendationContent,
    recommendation,
}: {
    fieldId?: string;
    onClose: (refetch: boolean) => void;
    platformRecommendationContent: ReactNode;
    recommendation: FieldRecommendation;
}) {
    const { useMutateRequest } = useRequest();
    const { t } = useTranslation();

    const [recommendApplication, setRecommendApplication] = useState<Selection>(
        new Set(["null"]),
    );
    const [notes, setNotes] = useState<string | null>(null);
    const [daysActive, setDaysActive] = useState<number | null>(5);

    useEffect(() => {
        if (recommendation.user_recommendation) {
            const edit = recommendation.user_recommendation;
            setNotes(edit.notes);
            setRecommendApplication(
                new Set([edit.recommend_application?.toString() ?? "null"]),
            );
            setDaysActive(edit.days_active ?? 5);
        }
    }, [recommendation.user_recommendation]);

    const editRecommendationRequest = useMutateRequest({
        url: `user_recommendations/upsert/`,
        method: "put",
        errorMessage: t("error.failed_to_edit_recommendation"),
        successMessage: t("success.edited_recommendation"),
    });

    const saveRecommendationEdit = useCallback(() => {
        editRecommendationRequest.mutate(
            {
                data: {
                    notes,
                    field: fieldId,
                    recommendation_date:
                        getTodayWithTimeTravel().format("YYYY-MM-DD"),
                    days_active: daysActive ?? 5,
                    recommend_application:
                        recommendApplication != "all"
                            ? recommendApplication.has("true")
                                ? true
                                : recommendApplication.has("false")
                                  ? false
                                  : null
                            : null,
                },
            },
            {
                onSuccess: () => {
                    onClose(true);
                },
            },
        );
    }, [
        editRecommendationRequest,
        notes,
        fieldId,
        daysActive,
        recommendApplication,
        onClose,
    ]);

    return (
        <Modal isOpen onClose={() => onClose(false)}>
            <ModalContent>
                <ModalHeader>Edit Recommendation</ModalHeader>
                <ModalBody>
                    <div className="flex flex-row gap-2 items-center">
                        <div className="font-bold">
                            Platform Recommendation:
                        </div>
                        <div>{platformRecommendationContent}</div>
                    </div>
                    <Divider />
                    <div className="flex flex-col gap-2 items-center">
                        <Select
                            label="New Recommendation"
                            placeholder=" "
                            selectedKeys={recommendApplication}
                            onSelectionChange={setRecommendApplication}
                            classNames={{
                                base: "min-w-[200px]",
                            }}
                            size="sm"
                            labelPlacement="outside"
                            renderValue={(items) => {
                                return items.map((item) => {
                                    if (item.key == "null") {
                                        return "No Change";
                                    }
                                    if (item.key === "true") {
                                        return <ApplyNChip />;
                                    }
                                    return <DoNotApplyNChip />;
                                });
                            }}
                        >
                            <SelectItem key={null}>No Change</SelectItem>
                            <SelectItem key={"true"}>
                                <ApplyNChip />
                            </SelectItem>
                            <SelectItem key={"false"}>
                                <DoNotApplyNChip />
                            </SelectItem>
                        </Select>
                        <Input
                            type="number"
                            label="Days Active"
                            value={daysActive?.toString() ?? ""}
                            onValueChange={(value) =>
                                setDaysActive(Number(value))
                            }
                            labelPlacement="outside"
                            max={7}
                            min={1}
                            size="sm"
                            endContent={<span className="text-xs">Days</span>}
                            classNames={{
                                description: "text-xs text-gray-500",
                            }}
                            description={
                                daysActive &&
                                `Recommendation will end on ${getTodayWithTimeTravel().add(daysActive, "day").format("MM/DD/YYYY")} `
                            }
                        />
                    </div>
                    <Textarea
                        labelPlacement="outside"
                        label="Notes"
                        size="sm"
                        minRows={4}
                        value={notes ?? ""}
                        onValueChange={setNotes}
                    />
                </ModalBody>
                <ModalFooter className="flex flex-row justify-end">
                    <Button
                        onPress={() => onClose(false)}
                        isDisabled={editRecommendationRequest.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onPress={saveRecommendationEdit}
                        isLoading={editRecommendationRequest.isPending}
                        isDisabled={editRecommendationRequest.isPending}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default UserRecommendationForm;
